import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import { hasUserPermission } from "helpers/ClaimHelpers";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import { useMemo, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  OrganizationAttributeTypeRequest,
  ResponseOfProduct,
  ResponseOfSkus,
  ResponseOfSkuStatus,
} from "services/KnowfullyAdminClient";
import { ClaimPermission, ClaimResource, ParentClaim } from "types/claimTypes";
import { AttributeDisplayTypes } from "types/common-types";
import { SkuFormProps, SkuSidebarSlots } from "types/sku-related-types";
import SkuAttributes from "./SkuAttributes";
import SkuAttributeAccordion from "./SkuAttributes/SkuAttributeAccordion";
import SkuRuleSection from "./SkuContentRules/SkuRuleSection";
import SkuDeliveryMethod from "./SkuDeliveryMethods";
import SkuDetailsCreateForm from "./SkuDetails/SkuDetailsCreateForm";
import SkuDetailsUpdateForm from "./SkuDetails/SkuDetailsUpdateForm";
import {
  isFieldVisible,
  SkuFieldKey,
} from "./SkuDetails/skuFieldVisibilityMap";
import SkuHandouts from "./SkuHandouts/index";
import SkuKnowledgeLevel from "./SkuKnowledgeLevels";
import SkuLearningLevel from "./SkuLearningLevels";
import SkuLearningObjectives from "./SkuLearningObjectives";
import SkuMajorTopics from "./SkuMajorTopics";
import SkuProgressionGates from "./SkuProgressionGates";
import SkuQualifications from "./SkuQualifications";
import SkuRelatedSku from "./SkuRelatedSkus";
import SkuResources from "./SkuResources";
import SkuStudyField from "./SkuStudyFields";
import InternalTags from "pages/SkusPage/InternalTags/InternalTags";
import SkuPageLayout from "pages/SkusPage/SkuSummarySidebar/SkuPageLayout";
import SkuPublishCard from "pages/SkusPage/SkuSummarySidebar/SkuPublishCard";

const useStyles = makeStyles((theme: Theme) => ({
  expandedPanel: {
    backgroundColor: theme.palette.secondary.light,
  },
}));

const SkuForm = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { formatMessage: t } = useIntl();
  const {
    skuDetailsUrl,
    skusUrl,
    productDetailsUrl,
    skuService,
    productService,
    attributeService,
  } = useGlobalContext();
  const styles = useStyles(useTheme());

  // `productId` defined by the skuCreateUrl route and is used when creating a new
  // SKU with the product pre-filled. We pass '-' for no product
  const { id, productId: productIdParam } = useParams<{
    id: string;
    productId: string;
  }>();

  const productId = productIdParam === "-" ? undefined : productIdParam;
  const isNew = !id;

  const [expandedSection, setExpandedSection] = useState<string | null>(
    !id ? "details" : null,
  );
  const [locked, lockedActions] = useBoolean(isNew);

  const editSkuAllowed = useMemo(
    () =>
      hasUserPermission(
        ParentClaim.Content,
        ClaimResource.Skus,
        ClaimPermission.Edit,
      ),
    [],
  );

  const skuResponse = useStrictQuery<ResponseOfSkus>(
    ["skuById", id],
    () => skuService.getSkuById(id!),
    {
      enabled: !!id,
    },
  );

  const [selectedProductId, setSelectedProductId] = useState<string | undefined>(undefined);
  const currentProductId =
    selectedProductId || productId || skuResponse?.data?.data?.productId!;

  const productResponse = useQuery<ResponseOfProduct>(
    ["productById", currentProductId],
    () => productService.getProductById(currentProductId),
    {
      enabled: !!currentProductId,
    },
  );

  const sku = skuResponse.data?.data;
  useTitle(isNew ? "New Sku" : sku?.sku || t({ id: "skuFormDetailsTitle" }));

  const skuStatusResponse = useQuery<ResponseOfSkuStatus>(
    ["skuService.getSkuStatusById", id, sku?.skuStatusId],
    () => skuService.getSkuStatusById(sku?.skuStatusId!),
    { enabled: !!id && !!sku?.skuStatusId },
  );

  const [statusSlot, setStatusSlot] = useState<HTMLDivElement | null>(null);
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);
  const sidebarSlots = useMemo<SkuSidebarSlots | null>(
    () =>
      statusSlot && actionsSlot
        ? { status: statusSlot, actions: actionsSlot }
        : null,
    [statusSlot, actionsSlot],
  );

  const request = OrganizationAttributeTypeRequest.fromJS({
    sortField: "name",
    organizationId: sku?.ownerOrganizationId,
    parameters: {
      attributeDisplayTypeId: AttributeDisplayTypes.Accordion,
    },
  });

  const organizationAttributeTypesResponse = useQuery(
    ["getOrganizationAttributeTypes", request],
    () => attributeService.getOrganizationAttributeTypes(request),
    {
      enabled: skuResponse.isFetched && sku !== undefined,
    },
  );

  if (!editSkuAllowed) {
    navigate(skuDetailsUrl(id));
  }

  if (skuResponse.isLoading) return <Loader />;
  if (productId && productResponse.isLoading) return <Loader />;

  const toggleExpansion = (section: string) => (e: any, expanded: boolean) => {
    if (locked || !id) return;
    setExpandedSection(expanded ? section : null);
  };

  const accordionProps = (section: string, disabled: boolean = false) => {
    return {
      square: true,
      expanded: expandedSection === section,
      onChange: toggleExpansion(section),
      disabled: (locked && expandedSection !== section) || disabled,
    };
  };

  const formProps = {
    onDirty: lockedActions.setTrue,
    onClean: lockedActions.setFalse,
    onProductIdChange: setSelectedProductId,
  };

  const invalidateDetailsQuery = (skuId?: string) => {
    if (product) {
      queryClient.invalidateQueries(["skusList"]);
      queryClient.invalidateQueries(["productById", currentProductId]);
    }

    if (skuId) {
      const queries = [
        "skuById",
        "product",
        "skuFormats",
        "SkuAvailabilities",
        "skuService.getSkuStatusById",
        "productYear",
        "version",
        "taxClass",
        "advancedPreparation",
        "corporateEntity",
        "resources",
        "skuRules",
        "skuRuleDetails",
        "webinar",
        "skuAttributes",
        "skuAttributesByType",
        "getSkuResources",
        "skuQualifications",
        "skuService.getSkuTags",
        "skuService.getChildSkusRelated",
      ];
      queries.forEach((query) => queryClient.invalidateQueries([query, skuId]));
    }

    queryClient.invalidateQueries(["webinarsList"]);
  };

  const onSkuCreated = (id: string) => {
    invalidateDetailsQuery(id);
    lockedActions.setFalse();
    navigate(skuDetailsUrl(id));
  };

  const onSkuUpdated = (id?: string) => {
    invalidateDetailsQuery(id);
    lockedActions.setFalse();
  };

  const onCancelCreate = () => {
    navigate(skusUrl());
  };

  const product = productResponse.data?.data;
  const organizationAttributeTypes =
    organizationAttributeTypesResponse.data?.data;

  const productTypeEnum = product?.productType?.enumValue;
  const isVisible = (key: SkuFieldKey) =>
    isFieldVisible(productTypeEnum, key, {
      skuAvailabilityId: sku?.skuAvailabilityId,
    });

  type AccordionConfig = {
    key: string;
    Component: React.FC<SkuFormProps> & any;
    title: string;
    extraProps?: any;
    visibilityKey?: SkuFieldKey;
  };

  const allPropertiesAccordions: AccordionConfig[] = [
    {
      key: "skuRelationships",
      Component: SkuRelatedSku,
      title: t({ id: "skuRelationshipsTitle" }),
      visibilityKey: "skuRelationships",
    },
    {
      key: "courseProgressionLogic",
      Component: SkuProgressionGates,
      title: t({ id: "courseProgressionLogicTitle" }),
      visibilityKey: "courseProgressionLogic",
    },
    {
      key: "knowledgeLevel",
      Component: SkuKnowledgeLevel,
      title: t({ id: "skuFormKnowledgeLevelTitle" }),
      visibilityKey: "knowledgeLevels",
    },
    {
      key: "learningLevel",
      Component: SkuLearningLevel,
      title: t({ id: "skuFormLearningLevelTitle" }),
      visibilityKey: "learningLevels",
    },
    {
      key: "studyField",
      Component: SkuStudyField,
      title: t({ id: "skuFormStudyFieldTitle" }),
      visibilityKey: "studyFields",
    },
    {
      key: "resources",
      Component: SkuResources,
      title: t({ id: "skuFacultyTitle" }),
      visibilityKey: "faculty",
    },
    {
      key: "majorTopics",
      Component: SkuMajorTopics,
      title: t({ id: "skuFormMajorTopicTitle" }),
      visibilityKey: "majorTopics",
    },
    {
      key: "qualifications",
      Component: SkuQualifications,
      title: t({ id: "accreditationBodies" }),
      visibilityKey: "qualifications",
    },
    {
      key: "courseHandouts",
      Component: SkuHandouts,
      title: t({ id: "courseHandouts" }),
      visibilityKey: "courseHandouts",
    },
    {
      key: "skuLearningObjectives",
      Component: SkuLearningObjectives,
      title: t({ id: "skuFormLearningObjectiveTitle" }),
      visibilityKey: "learningObjectives",
    },
    {
      key: "deliveryMethod",
      Component: SkuDeliveryMethod,
      title: t({ id: "skuFormDeliveryMethodTitle" }),
      visibilityKey: "contentDelivery",
    },
  ];

  const propertiesAccordions = allPropertiesAccordions.filter(
    (accordion) => !accordion.visibilityKey || isVisible(accordion.visibilityKey)
  );

  const attributesAccordions: AccordionConfig[] = [
    ...(organizationAttributeTypes
      ?.sort((a, b) => (a?.sequence! > b?.sequence! ? 1 : -1))
      .map((oat) => ({
        key: oat.attributeType!.name!,
        Component: SkuAttributeAccordion,
        title: oat.attributeType!.name!,
        extraProps: { organizationAttributeType: oat },
      })) ?? []),
    {
      key: "attributes",
      Component: SkuAttributes,
      title: t({ id: "otherLabel" }),
    },
  ];

  const sections = (
    <>
      {/* The primary details */}

      <Accordion {...accordionProps("details")}>
        <AccordionSummary
          style={{ margin: 0 }}
          expandIcon={<ExpandMoreIcon />}
          sx={accordionProps("details").expanded ? styles.expandedPanel : null}
        >
          <Typography>
            <T id="skuFormDetailsTitle" />
          </Typography>
        </AccordionSummary>

        <AccordionDetails>
          {expandedSection === "details" && !id && (
            <SkuDetailsCreateForm
              {...formProps}
              productId={productId}
              onSkuCreated={onSkuCreated}
              onCancel={onCancelCreate}
            />
          )}

          {expandedSection === "details" && id && sku && (
            <SkuDetailsUpdateForm
              sku={sku!}
              {...formProps}
              sidebarSlots={sidebarSlots}
              onSkuUpdated={onSkuUpdated}
            />
          )}
        </AccordionDetails>
      </Accordion>

      {isVisible("skuRules") && (
        <Tooltip
          title={
            !sku?.hasChild ? t({ id: "accordionSkuContentRulesDisabled" }) : ""
          }
        >
          <Accordion {...accordionProps("skuContentRules", !sku?.hasChild)}>
            <AccordionSummary
              style={{ margin: 0 }}
              expandIcon={<ExpandMoreIcon />}
              sx={
                accordionProps("skuContentRules").expanded
                  ? styles.expandedPanel
                  : null
              }
            >
              <Typography>
                <T id="skuContentRulesTitle" />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {expandedSection === "skuContentRules" && id && (
                <SkuRuleSection
                  sku={sku!}
                  onSkuUpdated={onSkuUpdated}
                  {...formProps}
                />
              )}
            </AccordionDetails>
          </Accordion>
        </Tooltip>
      )}

      <h2>
        <T id="propertiesLabel" />
      </h2>

      {propertiesAccordions.map(({ key, title, Component, extraProps }) => (
        <Accordion key={key} {...accordionProps(key)}>
          <AccordionSummary
            style={{ margin: 0 }}
            expandIcon={<ExpandMoreIcon />}
            sx={accordionProps(key).expanded ? styles.expandedPanel : null}
          >
            <Typography>{title}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {expandedSection === key && id && (
              <Component
                sku={sku!}
                {...formProps}
                {...extraProps}
                onSkuUpdated={onSkuUpdated}
              />
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      <h2>
        <T id="attributes" />
      </h2>

      {attributesAccordions.map(({ key, title, Component, extraProps }) => (
        <Accordion key={key} {...accordionProps(key)}>
          <AccordionSummary
            style={{ margin: 0 }}
            expandIcon={<ExpandMoreIcon />}
            sx={accordionProps(key).expanded ? styles.expandedPanel : null}
          >
            <Typography>{title}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {expandedSection === key && id && (
              <Component
                sku={sku!}
                {...formProps}
                {...extraProps}
                onSkuUpdated={onSkuUpdated}
              />
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  );

  return (
    <>
      <Box mb={3}>
        {!id ? (
          <RedirectBackButton
            redirectRoute={skusUrl()}
            labelKey="backToListButtonLabel"
          />
        ) : (
          <RedirectBackButton
            redirectRoute={skuDetailsUrl(id)}
            labelKey="backToDetailsButtonLabel"
          />
        )}
      </Box>

      {sku && (
        <Box mb={3}>
          <Typography variant="h5">{sku.sku}</Typography>
        </Box>
      )}

      {product && (
        <Box mb={3}>
          <Typography>
            <T
              id="skuProductLink"
              values={{
                productLink: (
                  <Link to={productDetailsUrl(product.id)}>
                    {product.computedName!}
                  </Link>
                ),
              }}
            />
          </Typography>
        </Box>
      )}

      {id ? (
        <SkuPageLayout
          sidebar={
            <Stack spacing={3}>
              <SkuPublishCard
                skuStatusId={sku?.skuStatusId}
                skuStatusName={skuStatusResponse.data?.data?.name}
                modifiedOn={sku?.modifiedOn}
                modifiedBy={sku?.modifiedBy}
                editSlots={
                  expandedSection === "details"
                    ? { status: setStatusSlot, actions: setActionsSlot }
                    : undefined
                }
              />
              <InternalTags skuId={id} />
            </Stack>
          }
        >
          {sections}
        </SkuPageLayout>
      ) : (
        sections
      )}
    </>
  );
};

export default SkuForm;
